import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  TagsInput,
  Text,
} from "@mantine/core";
import { useMemo, useState } from "react";
import {
  sendEmailWithFiles,
  sendEmail,
} from "../../../../../../api/WorkflowAPI";
import { notifications } from "@mantine/notifications";
import { useLanguage } from "../../../../../../stores/LanguageStore";
import {
  useAccountsStore,
  useConfigStore,
} from "../../../../../../stores/ConfigStore";
import {
  IconChevronDown,
  IconChevronUp,
  IconShare3,
} from "@tabler/icons-react";
import EmailAnswerEditorMobile from "../../answers/EmailAnswerEditorMobile";
import EmailAccountSelector from "../../../../../../components/Selectors/EmailAccountSelector";

const AnswerSenderMobile = ({
  prospect,
  item,
  about,
  setProspectLoading,
  onAnswerSent,
  prog,
  modelSubject,
  alreadyOpened,
  title = "Réponse",
  onClose,
}) => {
  const lang = useLanguage((s) => s.language);
  const accounts = useAccountsStore((s) => s.email_accounts);
  const signs = useConfigStore((s) => s.email_signatures);
  const [answering, setAnswering] = useState(false);
  const [ccOpened, setCcOpened] = useState(false);
  const [sender, setSender] = useState(
    item?.reply_to?.integration_id
      ? { id: item?.reply_to?.integration_id }
      : item?.integration_id
      ? { id: item?.integration_id }
      : accounts.length === 1
      ? accounts[0]
      : null
  );
  const [recipients, setRecipients] = useState([
    about?.email || "",
    ...(accounts
      ?.filter((x) => x.add_sender_as_recipient_in_replies)
      ?.map((item) => item?.email) || []),
  ]);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [sign, setSign] = useState(
    item?.reply_to?.signature_id
      ? { id: item?.reply_to?.signature_id }
      : signs.length === 1
      ? signs[0]
      : null
  );
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const subject = useMemo(() => {
    let html = item?.reply || item?.message || "";
    const match = html.match(/Subject\s*:\s*(.*?)<br>/i);
    let result = match?.[1].trim() || "";

    if (modelSubject || result) return `Re: ${modelSubject || result}`;
    return "";
  }, [item?.message, item?.reply, modelSubject]);

  const handleInit = (editor) => {
    setInitialized(true);

    if (sign) {
      editor.execCommand(
        "mceInsertContent",
        false,
        "<br /><br /><br /><br /><br /><br /><br /><br /><br /> %signature%"
      );
    }
  };

  const handleSubmit = ({ html, text, subject, files }) => {
    let oldHtml = item?.reply || item?.message;

    let newHtml = html;

    let parsedHtml = item
      ? `
      ${newHtml}
      <br />

      <div style="
          border: none;
          border-top: solid #e1e1e1 1pt;
          padding: 3pt 0in 0in 0in;
        ">
      </div>

      ${oldHtml || ""}
    `
      : newHtml;

    setLoading(true);

    let formData = new FormData();
    let data = {
      html: parsedHtml,
      text,
      subject,
      email_account_id: sender?.id,
      recipients,
      cc_recipients: ccRecipients,
      email_signature_id: sign?.id,
      // contact_id_prm:
      //   item?.id || item?.event === "email_sent" ? undefined : prospect?.id,
      // reply_id: item?.history_type === "sent" ? undefined : item?.id,
      // programmation_contact_id:
      //   item?.history_type === "sent" || item?.event === "email_sent"
      //     ? prog?.programmation_contact_id
      //     : undefined,
    };
    if (item?.history_type !== "sent" && item?.id) {
      data.reply_id = item?.id;
    }
    if (prog?.programmation_contact_id) {
      data.programmation_contact_id = prog?.programmation_contact_id;
    } else if (item?.event !== "email_sent") {
      data.contact_id_prm = prospect?.id;
    }
    Object.keys(data).forEach((key) => {
      if (key === "recipients") {
        return recipients.forEach((value) => {
          formData.append("recipients[]", value); // Use "myArray[]" to indicate an array
        });
      }
      if (key === "cc_recipients") {
        return ccRecipients.forEach((value) => {
          formData.append("cc_recipients[]", value); // Use "myArray[]" to indicate an array
        });
      }
      if (data[key]) formData.append(key, data[key]);
    });

    files?.forEach((file) => {
      formData.append("attachments[]", file); // Use "myArray[]" to indicate an array
    });

    let main = files.length > 0 ? sendEmailWithFiles : sendEmail;
    main(files.length > 0 ? formData : data)
      .then((res) => {
        notifications.show({
          message: lang.workflows.nodes.events.email_sent,
          color: "green.8",
        });
        setAnswering(false);
        setProspectLoading(true);
        onAnswerSent?.();
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setAnswering(false);
    onClose?.();
  };

  return (
    <>
      {!alreadyOpened && (
        <Button
          leftSection={<IconShare3 />}
          mx={8}
          mb={8}
          onClick={() => setAnswering(true)}
        >
          {lang.prm.answer}
        </Button>
      )}

      <Modal
        opened={alreadyOpened || answering}
        title={title}
        onClose={handleClose}
        size={"100%"}
      >
        <LoadingOverlay visible={loading || !initialized} />

        <Group wrap="nowrap" gap={"xs"} pos={"relative"}>
          <Text w={32} size="sm">
            From
          </Text>

          <EmailAccountSelector
            size={"sm"}
            required
            onChange={(s) => {
              setSender(s);

              if (s?.email_signatures_profile) {
                setSign(s.email_signatures_profile);
              }
            }}
            value={sender?.id}
            smtpOnly={"semi"}
            styles={{ input: { border: 0 } }}
          />
        </Group>

        <Divider opacity={0.5} mb={10} />

        <Group wrap="nowrap" gap={"xs"} pos={"relative"}>
          <Text w={32} size="sm">
            To
          </Text>
          <TagsInput
            type="email"
            value={recipients}
            onChange={setRecipients}
            placeholder={"email"}
            flex={1}
            required
            variant="transparent"
            p={0}
            styles={{
              input: {
                padding: 0,
                height: "auto",
                minHeight: 0,
              },
            }}
          />
          <ActionIcon
            onClick={() => setCcOpened(!ccOpened)}
            variant="subtle"
            style={{ alignSelf: "center", transform: "translateY(-50%)" }}
            pos={"absolute"}
            top={"50%"}
            right={0}
          >
            {ccOpened ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </ActionIcon>
        </Group>

        <Divider opacity={0.5} my={10} />

        {ccOpened && (
          <>
            <Group wrap="nowrap" gap={"xs"}>
              <Text w={32} size="sm">
                Cc
              </Text>
              <TagsInput
                type="email"
                value={ccRecipients}
                onChange={setCcRecipients}
                placeholder={"Cc"}
                flex={1}
                styles={{
                  input: {
                    padding: 0,
                    height: "auto",
                    minHeight: 0,
                  },
                }}
                variant="transparent"
              />
            </Group>
            <Divider opacity={0.5} my={10} />
          </>
        )}

        <EmailAnswerEditorMobile
          key={item?.id}
          id={item?.id}
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          onInit={handleInit}
          loading={loading}
          disabled={!sender || recipients.length === 0}
          sign={sign?.id}
          defaultSubject={
            item?.reply_to?.subject ? `Re : ${item.reply_to.subject}` : subject
          }
        />
      </Modal>
    </>
  );
};

export default AnswerSenderMobile;
